import type { AuthPayload } from '@app'
import type {
  UpdateMapLayerPayload,
  UpdateMapNamePayload,
  UpdateMapZoomPayload,
  WeatherData,
  YachtTime
} from '@constants/types'
import type { MapMode } from '@shared_types/global_types'
import type { MapboxLayers, WindyLayers } from '@shared_types/map_types'
import { type Metric } from '@shared_types/vessel_data'
import { callApi } from '@utils/call_api'
import { snakeCase } from 'lodash'
import type { Moment } from 'moment'
import type { Dispatch } from 'redux'

import { FETCH_COMPLETE, FETCH_ERROR, FETCH_START } from './fetch'

export const LOAD_MAIN = 'LOAD_MAIN'
export const UPDATE_MAIN = 'UPDATE_MAIN'

export const UPDATE_MAIN_SPEED = 'UPDATE_MAIN_SPEED'
export const UPDATE_MAIN_HEADING = 'UPDATE_MAIN_HEADING'
export const UPDATE_MAIN_DEPTH = 'UPDATE_MAIN_DEPTH'
export const UPDATE_MAIN_PRESSURE = 'UPDATE_MAIN_PRESSURE'
export const UPDATE_MAIN_WIND_SPEED = 'UPDATE_MAIN_WIND_SPEED'
export const UPDATE_MAIN_WIND_DIRECTION = 'UPDATE_MAIN_WIND_DIRECTION'
export const UPDATE_MAIN_AIR_TEMPERATURE = 'UPDATE_MAIN_AIR_TEMPERATURE'
export const UPDATE_MAIN_SEA_WATER_TEMPERATURE = 'UPDATE_MAIN_SEA_WATER_TEMPERATURE'
export const UPDATE_MAIN_TOTAL_DISTANCE = 'UPDATE_MAIN_TOTAL_DISTANCE'
export const UPDATE_MAIN_AVG_SPEED = 'UPDATE_MAIN_AVG_SPEED'
export const UPDATE_MAIN_MAX_SPEED = 'UPDATE_MAIN_MAX_SPEED'
export const UPDATE_MAIN_ECO_SCORE = 'UPDATE_MAIN_ECO_SCORE'

export const LOAD_MAIN_POSITION = 'LOAD_MAIN_POSITION'
export const UPDATE_MAIN_POSITION = 'UPDATE_MAIN_POSITION'

export const LOAD_HISTORY = 'LOAD_HISTORY'
export const UPDATE_HISTORY = 'UPDATE_HISTORY'

export const LOAD_PROPELLERS = 'LOAD_PROPELLERS'
export const UPDATE_PROPELLERS = 'UPDATE_PROPELLERS'

export const LOAD_RUDDER = 'LOAD_RUDDER'
export const UPDATE_RUDDER = 'UPDATE_RUDDER'

export const LOAD_GEARBOX = 'LOAD_GEARBOX'
export const UPDATE_GEARBOX = 'UPDATE_GEARBOX'

export const LOAD_CHILLER = 'LOAD_CHILLER'
export const UPDATE_CHILLER = 'UPDATE_CHILLER'

export const LOAD_ENGINES = 'LOAD_ENGINES'
export const UPDATE_ENGINES = 'UPDATE_ENGINES'

export const LOAD_GENERATORS = 'LOAD_GENERATORS'
export const UPDATE_GENERATORS = 'UPDATE_GENERATORS'

export const LOAD_TANKS = 'LOAD_TANKS'
export const UPDATE_TANKS = 'UPDATE_TANKS'

export const LOAD_WATERMAKERS = 'LOAD_WATERMAKERS'
export const UPDATE_WATERMAKERS = 'UPDATE_WATERMAKERS'

export const LOAD_STABILIZERS = 'LOAD_STABILIZERS'
export const UPDATE_STABILIZERS = 'UPDATE_STABILIZERS'

export const LOAD_THRUSTER = 'LOAD_THRUSTER'
export const UPDATE_THRUSTER = 'UPDATE_THRUSTER'

export const LOAD_SAT_ANTENNAS = 'LOAD_SAT_ANTENNAS'
export const UPDATE_SAT_ANTENNAS = 'UPDATE_SAT_ANTENNAS'

export const UPDATE_TV_MODE = 'UPDATE_TV_MODE'

export const UPDATE_OVAL_MODE = 'UPDATE_OVAL_MODE'
export const UPDATE_OVAL_WIDTH = 'UPDATE_OVAL_WIDTH'

export const UPDATE_TEMP_UNIT = 'UPDATE_TEMP_UNIT'

export const UPDATE_TEMP_UNIT_GLOBAL = 'UPDATE_TEMP_UNIT_GLOBAL'

export const UPDATE_AUTH = 'UPDATE_AUTH'

export const UPDATE_ROUTE = 'UPDATE_ROUTE'

export const UPDATE_ACTIVE_MAP = 'UPDATE_ACTIVE_MAP'
export const UPDATE_ACTIVE_LAYER = 'UPDATE_ACTIVE_LAYER'
export const UPDATE_MAP_ZOOM = 'UPDATE_MAP_ZOOM'

export const UPDATE_TIME = 'UPDATE_TIME'

export const UPDATE_WEATHER_DATA = 'UPDATE_WEATHER_DATA'

export function action (type: string, resource: string, status: string, payload = {}, error = null) {
  return { type, resource, meta: { fetchStatus: status }, payload, error }
}

export const getAction = {
  request: (type: string, resource: string) => action(type, resource, FETCH_START),
  // @ts-expect-error leave as is
  success: (type: string, resource: string, payload: unknown) => action(type, resource, FETCH_COMPLETE, payload),
  // @ts-expect-error leave as is
  failure: (type: string, resource: string, error: unknown) => action(type, resource, FETCH_ERROR, {}, error)
}

export const load = (resource: string) => {
  return async function (dispatch: Dispatch) {
    const normalizedResource = snakeCase(resource).toUpperCase()
    const type = `LOAD_${normalizedResource}`

    try {
      const data = await callApi(`/api/${resource}`)
      return dispatch(getAction.success(type, resource, data))
    } catch (error) {
      return dispatch(getAction.failure(type, resource, error))
    }
  }
}

export const loadHistory = (resource: string, params?: { limit?: number }) => {
  return async (dispatch: Dispatch) => {
    const normalizedResource = snakeCase(resource).toUpperCase()
    const type = `${LOAD_HISTORY}_${normalizedResource}`
    const url = `/api/${resource}${params?.limit ? '?limit=' + params.limit : ''}`

    try {
      const data = await callApi(url)
      dispatch(getAction.success(type, normalizedResource, { [resource]: data }))
    } catch (error) {
      dispatch(getAction.failure(type, normalizedResource, error))
    }
  }
}

export const update = (resource: string, o: unknown) => {
  return (dispatch: Dispatch) => {
    const normalizedResource = snakeCase(resource).toUpperCase()
    const type = `${UPDATE_MAIN}_${normalizedResource}`
    return dispatch(getAction.success(type, normalizedResource, o))
  }
}

export const updatePage = (resource: string, o: unknown) => {
  return (dispatch: Dispatch) => {
    const normalizedResource = snakeCase(resource).toUpperCase()
    const type = `UPDATE_${normalizedResource}`
    return dispatch(getAction.success(type, normalizedResource, o))
  }
}

export const updateHistory = (resource: string, o: Metric[]) => {
  return (dispatch: Dispatch) => {
    const normalizedResource = snakeCase(resource).toUpperCase()
    const type = `${UPDATE_HISTORY}_${normalizedResource}`
    return dispatch(getAction.success(type, normalizedResource, { [resource]: o }))
  }
}

export const updateTvMode = (resource: string, o: boolean) => {
  return (dispatch: Dispatch) => {
    const normalizedResource = snakeCase(resource).toUpperCase()
    const type = `UPDATE_${normalizedResource}`
    return dispatch(getAction.success(type, normalizedResource, o))
  }
}

export const updateOvalMode = (resource: string, o: boolean) => {
  return (dispatch: Dispatch) => {
    const normalizedResource = snakeCase(resource).toUpperCase()
    const type = `UPDATE_${normalizedResource}`
    return dispatch(getAction.success(type, normalizedResource, o))
  }
}

export const updateBlockedRoute = (resource: string, o: boolean) => {
  return (dispatch: Dispatch) => {
    const normalizedResource = snakeCase(resource).toUpperCase()
    const type = `UPDATE_${normalizedResource}`
    return dispatch(getAction.success(type, normalizedResource, o))
  }
}

export const updateUnit = (resource: string, o: string) => {
  return function (dispatch: Dispatch) {
    const normalizedResource = snakeCase(resource).toUpperCase()
    const type = `UPDATE_${normalizedResource}`
    return dispatch(getAction.success(type, normalizedResource, o))
  }
}

export const updateAuth = (resource: 'auth', payload: AuthPayload) => {
  return (dispatch: Dispatch) => {
    const normalizedResource = snakeCase(resource).toUpperCase()
    const type = `UPDATE_${normalizedResource}`
    return dispatch(getAction.success(type, normalizedResource, payload))
  }
}

export const updateWeather = (resource: 'weather_data', payload: Partial<WeatherData>) => {
  return (dispatch: Dispatch) => {
    const normalizedResource = snakeCase(resource).toUpperCase()
    const type = `UPDATE_${normalizedResource}`
    return dispatch(getAction.success(type, normalizedResource, payload))
  }
}

export const updateMapName = (
  activeMap: MapMode
): {
  type: typeof UPDATE_ACTIVE_MAP
  payload: UpdateMapNamePayload
} => ({
  type: UPDATE_ACTIVE_MAP,
  payload: { activeMap }
})

export const updateMapLayer = (
  map: MapMode,
  layer: WindyLayers | MapboxLayers
): {
  type: typeof UPDATE_ACTIVE_LAYER
  payload: UpdateMapLayerPayload
} => ({
  type: UPDATE_ACTIVE_LAYER,
  payload: { map, layer }
})

export const updateMapZoom = (
  zoom: number
): {
  type: typeof UPDATE_MAP_ZOOM
  payload: UpdateMapZoomPayload
} => ({
  type: UPDATE_MAP_ZOOM,
  payload: { zoom }
})

export type UpdateMapConfigAction
  = | ReturnType<typeof updateMapName>
  | ReturnType<typeof updateMapLayer>
  | ReturnType<typeof updateMapZoom>

export const updateTime = (
  value: Moment,
  model: string
): {
  type: typeof UPDATE_TIME
  payload: YachtTime
} => ({
  type: UPDATE_TIME,
  payload: { value, model }
})

export type UpdateTimeConfig = ReturnType<typeof updateTime>
